import { useMemo } from 'react'
import useSWR from 'swr'

import { getBookingLeads } from '@/api/booking'
import { useArtistScopeSafe } from '@/lib/artistContext'

export type BookingLeadRecord = Awaited<ReturnType<typeof getBookingLeads>> extends Array<infer T> ? T : never

export function useBookingLeads(options?: { status?: string; limit?: number }) {
  const { status, limit = 200 } = options ?? {}
  const { selectedArtistId } = useArtistScopeSafe()
  // Artist-scoped cache key so one artist's leads are never served to another.
  const cacheKey = ['booking/leads', selectedArtistId ?? 'none', status, limit]

  const { data, error, mutate, isLoading } = useSWR(cacheKey, () => getBookingLeads({ status, limit }))

  const summary = useMemo(() => {
    if (!data) return { total: 0, confirmed: 0, pending: 0 }
    const total = data.length
    const confirmed = data.filter((lead) => lead.status?.toLowerCase() === 'confirmed').length
    const pending = data.filter((lead) => lead.status?.toLowerCase() === 'pending').length
    return { total, confirmed, pending }
  }, [data])

  return {
    leads: data ?? [],
    loading: isLoading,
    error,
    refresh: mutate,
    summary,
  }
}
